import React, { useState, forwardRef, useEffect, useImperativeHandle, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components'
import { Input, Table, Button, Modal, notification } from 'antd';
import UserForm from './UserForm';
import Drawer from '../../Drawer';
import { Icon, ThemeProvider } from 'dlweb-components';
import { showModal } from '../../../store/actions';
import WorkflowReplacementPicker from '../../modals/WorkflowReplacementPicker';
import _ from 'lodash';
import UserGrid from './UserGrid';

const UserManager = forwardRef(({setMenuItems, showModal, rootScope, location, isAdmin}, ref) => {

  const gridRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const [showReplacementDialog, setShowReplacementDialog] = useState(false);
  const [usersInWorkflow, setUsersInWorkflow] = useState([]);
  const [replacementUser, setReplacementUser] = useState('');

  const [adminSettings, setAdminSettings] = useState({});

  const defaultButtons = useMemo(() => 
    {
      const buttonAray = [
        {
          key: 'new-sql-user',
          action: 'new-sql-user',
          icon: <Icon type='user-plus' fixedWidth="25px" />,
          label: 'New SQL User'
        }
      ]

    if(adminSettings.activeDirectory?.enabled && isAdmin) {
      const adButton = {
        key: 'new-ad-user',
        action: 'new-ad-user',
        icon: <Icon type='user-plus' fixedWidth="25px" />,
        label: 'New AD User'
      }

      buttonAray.push(adButton);
    }

    return buttonAray;
  }, [adminSettings, isAdmin]);

  const [menuButtons, setMenuButtons] = useState(defaultButtons);

  useEffect(() => {
    axios.post('/api/settings/read/dto/wt').then((res) => {
      setAdminSettings(res.data.data)
    })
  }, [setAdminSettings])

  const refreshUsers = useCallback((resetPage) => {
    gridRef.current.refresh(resetPage);
  }, [currentFilters]);

  const postDelete = useCallback((replacement) => {

    const selectedUserIds = gridRef.current.getSelectedUserIds();

    const ids = _.filter(selectedUserIds, o => o !== 'cssadmin');

    axios.post('/api/users/delete', { ids: ids, replacementId: replacement }).then(res => {
      if(res.data.errors.length > 0) {
        Modal.error({content: res.data.errors[0]});
        refreshUsers(true);
      } else {
        refreshUsers(true);
        setVisible(false);
        const multi = selectedUserIds > 1;
        notification.success({ message: `User${multi ? 's' : ''} deleted successfully.` });
        gridRef.current.clearSelectedUsers();
      }

      setShowReplacementDialog(false)
    });
  }, [refreshUsers])

  const onReplacementOk = () => {
    postDelete(replacementUser);
  }

  const deleteSelectedUsers = useCallback(() => {
    
    const selectedUsers = gridRef.current.getSelectedUsers();

    const continueDelete = () => {

      const uiw = _.filter(selectedUsers, o => o.id !== 'cssadmin' && o.inWorkflow);

      if(uiw.length > 0) {
        setUsersInWorkflow(uiw.map(o => o.id));
        setShowReplacementDialog(true);
        return;
      }
      else {
        postDelete();
      }
    }

    if(_.find(selectedUsers, { id: 'cssadmin' })) {
      Modal.info({
        title: 'Cannot delete cssadmin',
        content: `User 'cssadmin' is selected, but will not be deleted.`,
        onOk: continueDelete
      });
    }
    else {
      continueDelete();
    }
  }, [postDelete])

  const openDeleteConfirmation = useCallback(() => {

    const selectedUsers = gridRef.current.getSelectedUsers();

    Modal.confirm({
      icon: null,
      title: <>
                <Icon type='exclamation-circle' size="lg" style={{color: 'var(--warning-color)', marginRight: '10px'}}/>
                Delete the following users?
             </>,
      content: <div>
                  <ul style={{listStyle: 'none', margin: '20px 0', padding: 0, maxHeight: '200px', overflowY: 'auto'}}>
                    {selectedUsers.map((item) => (
                      <li key={item.id}>{item.id}</li>
                    ))}
                  </ul>
                </div>,
      onOk: deleteSelectedUsers
    });
  }, [deleteSelectedUsers])

  useImperativeHandle(ref, () => ({
    onButtonClick(action) {
      switch (action) {
        case 'new-sql-user':
          
          const sqlOptions = {
            title: 'New SQL User',
            maskClosable: false,
            footer: null,
            width: '800px',
          };

          showModal('NEW_SQL_USER', sqlOptions, { onSuccess: refreshUsers });
        break;
        case 'new-ad-user':
          
          const adOptions = {
            title: 'New AD User',
            maskClosable: false,
            footer: null,
            width: '850px',
            style: {
              minWidth: '700px'
            }
          };

          showModal('NEW_AD_USER', adOptions, { adminSettings, onSuccess: refreshUsers });
        break;
        case 'delete-user':
          openDeleteConfirmation();
          break;
        default:
          break;
      }
    },
  }))

  const cssadminSelected = useCallback(() => {
    const selectedUsers = gridRef.current.getSelectedUsers();
    return selectedUsers.length === 1 && selectedUsers[0]?.id === 'cssadmin';
  }, [gridRef]);

  useEffect(() => {
    const filters = { limit: 50, skip: 0};

    setCurrentFilters(filters);

  }, [setMenuItems, menuButtons])

  const userMenu = () => [
    {
      key: "delete-user",
      action: "delete-user",
      data: "delete-user-data",
      icon: <Icon type='user-slash' fixedWidth="25px" />,
      label: `Delete User${gridRef.current?.getSelectedUserIds().length > 1 ? 's' : ''}`,
    },
  ]

  const onSelect = useCallback((items) => {

    if(items.length > 0) {
      setVisible(true);
      setMenuItems(isAdmin && !cssadminSelected() ? userMenu : []);
      setMenuButtons(isAdmin && !cssadminSelected() ? userMenu : []);
    }
    else {
      setVisible(false);
      setMenuItems(isAdmin ? defaultButtons : []);
      setMenuButtons(isAdmin ? defaultButtons : []);
    }
  }, [setMenuItems, isAdmin, cssadminSelected, defaultButtons])

  const openDrawer = () => {
    setVisible(true);
  }

  const onClose = () => {
    setVisible(false);
  }

  const closeReplacementUserDialog = () => {
    setShowReplacementDialog(false);
    setReplacementUser('');
  }

  const onContextClick = (action) => {
    if(action === 'delete-user') {
      openDeleteConfirmation();
    }
  }

  return(
    <ThemeProvider>
      <WorkflowReplacementPicker
        onSelect={setReplacementUser}
        onCancel={closeReplacementUserDialog}
        visible={showReplacementDialog}
        membersInWorkflow={usersInWorkflow}
        userIds={gridRef.current?.getSelectedUserIds()}
        onOk={onReplacementOk}
      />
      <FlexContainer>
        <TableContainer>
          <UserGrid
            ref={gridRef}
            size="middle"
            onSelect={onSelect}
            contextMenu={menuButtons}
            onUserIdClick={openDrawer}
            onContextClick={onContextClick}
          />
          </TableContainer>
          <Drawer visible={visible} onClose={onClose}>
            <UserForm
              userIds={gridRef.current?.getSelectedUserIds() || []}
              rootScope={rootScope}
              location={location}
              isAdmin={isAdmin}
              shouldLoad={visible}
              refreshUsers={refreshUsers}
            />
          </Drawer>
      </FlexContainer>
    </ThemeProvider>
  )
})

const FlexContainer = styled.div`
  display: flex;
`
const TableContainer = styled.div`
  flex: auto;
  min-width: 300px;
  .ant-table {
    border-bottom: 1px solid #dee0e3;
  }
  .ant-table-body,
  .ant-table-tbody,
  .ant-table-content,
  .ant-table-placeholder,
  .ant-table-expanded-row-fixed {
    overflow: auto !important;
    min-height: calc(100vh - 240px);
  }
  .ant-pagination {
    float: left;
    margin: 6px 0 0 15px;
  }
`
const mapStateToProps = state => {
  const { isAdmin } = state.user;
  return { isAdmin };
};

const mapDispatchToProps = { showModal };

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(UserManager);